import { Button, DropdownMenu, SvgIconNames } from '@toggle/design-system';
import React, { ReactNode } from 'react';

import * as S from './MenuBarV2.styles';

interface MenuBarV2Item {
  id: ChartMenuBarV2Ids;
  iconName: SvgIconNames;
}

export interface MenuBarV2Locales {
  addData: string;
  marketLeadingIndicators: string;
  insights: string;
}

interface MenuBarV2Props {
  onPrimarySearch: () => void;
  onMenuItemClick: (id: ChartMenuBarV2Ids) => void;
  disabledItems: boolean;
  children?: ReactNode;
  locales: MenuBarV2Locales;
  insightsDropdown?: ReactNode;
  isInsightsDropdownOpen?: boolean;
  onInsightsDropdownOpenChange?: (open: boolean) => void;
}

export type ChartMenuBarV2Ids = 'marketLeadingIndicators' | 'insights';

export const MenuBarV2 = ({
  onPrimarySearch,
  onMenuItemClick,
  disabledItems,
  children,
  locales,
  insightsDropdown,
  isInsightsDropdownOpen,
  onInsightsDropdownOpenChange,
}: MenuBarV2Props) => {
  const items: MenuBarV2Item[] = [
    {
      id: 'marketLeadingIndicators',
      iconName: 'Indicators',
    },
    {
      id: 'insights',
      iconName: 'ExploreNav',
    },
  ];

  const renderMenuItem = (item: MenuBarV2Item) => {
    const button = (
      <S.StyledButton
        iconName={item.iconName}
        label={locales[item.id]}
        variant="empty"
        onClick={() => {
          if (item.id !== 'insights' || !insightsDropdown) {
            onMenuItemClick(item.id);
          }
        }}
        disabled={disabledItems}
        data-testid={`${item.id}-button`}
      />
    );

    if (item.id === 'insights' && !!insightsDropdown) {
      return (
        <DropdownMenu
          triggerNode={button}
          open={isInsightsDropdownOpen}
          onOpenChange={e => {
            if (!disabledItems) {
              return onInsightsDropdownOpenChange?.(e);
            }
          }}
          align="start"
          modal={false}
        >
          {insightsDropdown}
        </DropdownMenu>
      );
    }

    return button;
  };

  return (
    <S.MenuBarRoot data-testid="menu-bar">
      {children && <S.MenuItem>{children}</S.MenuItem>}
      <S.MenuItemSearch>
        <Button
          data-testid="search-button"
          iconName="Add"
          label={locales.addData}
          variant="empty"
          fullWidth
          aria-label={locales.addData}
          onClick={onPrimarySearch}
        />
      </S.MenuItemSearch>
      {items.map(item => (
        <S.MenuItem key={item.iconName}>{renderMenuItem(item)}</S.MenuItem>
      ))}
    </S.MenuBarRoot>
  );
};
